import type { AccountProfile, MapLibraryRecord, MdmMap } from "@marshmallow-duel/simulation";

export class ApiError extends Error {
  constructor(readonly code: string) {
    super(code);
  }
}

const request = async <T,>(path: string, init?: RequestInit): Promise<T> => {
  let response: Response;
  try {
    response = await fetch(path, {
      ...init,
      credentials: "same-origin",
      headers: init?.body ? { "Content-Type": "application/json", ...init.headers } : init?.headers
    });
  } catch {
    throw new ApiError("networkError");
  }
  const body = await response.json().catch(() => ({ ok: false, error: "internalError" })) as { ok?: boolean; error?: string } & T;
  if (!response.ok || body.ok !== true) throw new ApiError(body.error ?? "internalError");
  return body;
};

export const getCurrentProfile = async (): Promise<AccountProfile | null> => {
  try {
    const result = await request<{ profile: AccountProfile }>("/api/auth/me");
    return result.profile;
  } catch (error) {
    if (error instanceof ApiError && error.code === "unauthorized") return null;
    throw error;
  }
};

export const loginWithSplatch = async (ticket: string): Promise<AccountProfile> => {
  const result = await request<{ profile: AccountProfile }>("/api/auth/splatch", { method: "POST", body: JSON.stringify({ ticket }) });
  return result.profile;
};

export const logoutAccount = async (): Promise<void> => {
  await request<Record<string, never>>("/api/auth/logout", { method: "POST", body: "{}" });
};

export const updateProfile = async (input: { readonly name: string; readonly color: number; readonly skin: number }): Promise<AccountProfile> => {
  const result = await request<{ profile: AccountProfile }>("/api/profile", { method: "PATCH", body: JSON.stringify(input) });
  return result.profile;
};

export const listMaps = async (options: { readonly playerCount?: number; readonly mine?: boolean; readonly query?: string } = {}): Promise<readonly MapLibraryRecord[]> => {
  const parameters = new URLSearchParams();
  if (options.playerCount) parameters.set("players", String(options.playerCount));
  if (options.mine) parameters.set("mine", "true");
  if (options.query) parameters.set("q", options.query);
  const result = await request<{ maps: readonly MapLibraryRecord[] }>(`/api/maps?${parameters}`);
  return result.maps;
};

export const publishMap = async (map: MdmMap, description: string, language: "cz" | "en"): Promise<MapLibraryRecord> => {
  const result = await request<{ map: MapLibraryRecord }>("/api/maps", {
    method: "POST",
    body: JSON.stringify({ name: map.name, description, language, map, published: true })
  });
  return result.map;
};

export const recordMapPlay = async (id: string): Promise<void> => {
  await request<Record<string, never>>(`/api/maps/${encodeURIComponent(id)}/play`, { method: "POST", body: "{}" });
};
